'use client';

import React, { useState, useId, useContext, createContext, useCallback, useRef } from 'react';
import './Accordion.css';

export interface AccordionItem {
  /** Unique identifier for the accordion item */
  id: string;
  /** Accordion header/trigger label */
  label: React.ReactNode;
  /** Accordion panel content */
  content: React.ReactNode;
  /** Whether the item is disabled */
  disabled?: boolean;
}

export interface AccordionProps {
  /** Array of accordion item definitions */
  items: AccordionItem[];
  /** Controlled: currently expanded item IDs */
  expandedItems?: string[];
  /** Uncontrolled: initial expanded item IDs */
  defaultExpandedItems?: string[];
  /** Allow multiple items open simultaneously (default: false) */
  allowMultiple?: boolean;
  /** Icon style for expand/collapse indicator (default: 'chevron'). `'arrow'` is
   *  the two-bar chevron used by the legacy presales FAQ. */
  iconStyle?: 'chevron' | 'plus-minus' | 'arrow';
  /** Panel open/close animation (default: 'collapse' — grid-rows height).
   *  `'slide-fade'` ports the legacy presales FAQ motion (max-height + opacity +
   *  translateY) and a per-item flip-in on mount. */
  animation?: 'collapse' | 'slide-fade';
  /** Also toggle an item when its open panel content is clicked (default: false),
   *  not just the header — the legacy presales FAQ behaviour. Clicks on
   *  interactive elements inside the content (links, buttons, form fields) are
   *  ignored so they keep working. The header button remains the accessible
   *  control; this is a mouse-only convenience. */
  toggleOnContentClick?: boolean;
  /** Whether panels are `role="region"` landmarks. Omitted ⇒ automatic:
   *  regions up to 6 items, none above (see REGION_ROLE_MAX_ITEMS). Pass
   *  `false` when composing several accordions on one page whose combined
   *  panel count would flood landmark navigation even though each accordion
   *  is under the limit (ComparisonTable's mobile product cards); `true`
   *  forces regions regardless of count. */
  regionPanels?: boolean;
  /** Called when expanded items change */
  onExpandedItemsChange?: (itemIds: string[]) => void;
  /** Custom class for container */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
}

export interface AccordionGroupProps {
  /** Accordion components to coordinate */
  children: React.ReactNode;
  /** Custom class for container */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
}

// Panels are `role="region"` landmarks so screen-reader users can jump to an
// expanded panel's content — but the ARIA APG advises against it above ~6
// panels, where one accordion floods the landmark list (24 on the web-hosting
// all-features page) and makes landmark navigation useless. Above this limit
// the panels render with no role; aria-labelledby/aria-controls still tie each
// panel to its trigger.
const REGION_ROLE_MAX_ITEMS = 6;

// Context for AccordionGroup coordination
interface AccordionGroupContextValue {
  activeAccordionId: string | null;
  activeItemId: string | null;
  setActive: (accordionId: string, itemId: string | null) => void;
}

const AccordionGroupContext = createContext<AccordionGroupContextValue | null>(null);

export const AccordionGroup: React.FC<AccordionGroupProps> = ({
  children,
  className = '',
  style,
}) => {
  const [activeAccordionId, setActiveAccordionId] = useState<string | null>(null);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);

  const setActive = useCallback((accordionId: string, itemId: string | null) => {
    setActiveAccordionId(accordionId);
    setActiveItemId(itemId);
  }, []);

  return (
    <AccordionGroupContext.Provider value={{ activeAccordionId, activeItemId, setActive }}>
      <div
        className={`hi-ui-accordion-group ${className}`.trim()}
        style={style}
        data-testid="accordion-group"
      >
        {children}
      </div>
    </AccordionGroupContext.Provider>
  );
};

// Expand/collapse indicator. `arrow` is a CSS-only two-bar chevron (styled via
// pseudo-elements in Accordion.css); the rotation is driven by the item's
// `--expanded` modifier, so it needs no per-state markup.
const renderAccordionIcon = (
  iconStyle: NonNullable<AccordionProps['iconStyle']>,
  expanded: boolean
) => {
  if (iconStyle === 'arrow') {
    return <span className="hi-ui-accordion__arrow" aria-hidden="true" />;
  }

  if (iconStyle === 'plus-minus') {
    return expanded ? (
      <svg
        className="hi-ui-accordion__icon"
        width="12"
        height="4"
        viewBox="0 0 12 4"
        fill="none"
        aria-hidden="true"
      >
        <rect y="0.5" width="12" height="3" rx="1.5" fill="currentColor" />
      </svg>
    ) : (
      <svg
        className="hi-ui-accordion__icon"
        width="11"
        height="11"
        viewBox="0 0 11 11"
        fill="none"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M9.625 4.125H6.875V1.375C6.875 0.615312 6.25934 0 5.5 0C4.74066 0 4.125 0.615312 4.125 1.375V4.125H1.375C0.615656 4.125 0 4.74031 0 5.5C0 6.25969 0.615656 6.875 1.375 6.875H4.125V9.625C4.125 10.3847 4.74066 11 5.5 11C6.25934 11 6.875 10.3847 6.875 9.625V6.875H9.625C10.3843 6.875 11 6.25969 11 5.5C11 4.74031 10.3843 4.125 9.625 4.125Z"
          fill="currentColor"
        />
      </svg>
    );
  }

  return (
    <svg
      className="hi-ui-accordion__chevron"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M5 7.5L10 12.5L15 7.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const Accordion: React.FC<AccordionProps> = ({
  items,
  expandedItems,
  defaultExpandedItems = [],
  allowMultiple = false,
  iconStyle = 'chevron',
  animation = 'collapse',
  toggleOnContentClick = false,
  regionPanels,
  onExpandedItemsChange,
  className = '',
  style,
}) => {
  const panelIsRegion = regionPanels ?? items.length <= REGION_ROLE_MAX_ITEMS;
  const generatedId = useId();
  const accordionId = `hi-ui-accordion-${generatedId}`;
  const groupContext = useContext(AccordionGroupContext);
  const triggerRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Internal state for uncontrolled mode
  const [internalExpandedItems, setInternalExpandedItems] =
    useState<string[]>(defaultExpandedItems);

  // Determine if controlled
  const isControlled = expandedItems !== undefined;

  // Get current expanded items based on context or local state
  const getCurrentExpandedItems = (): string[] => {
    if (groupContext) {
      // In a group: only this accordion's active item is expanded
      if (groupContext.activeAccordionId === accordionId && groupContext.activeItemId) {
        return [groupContext.activeItemId];
      }
      return [];
    }
    return isControlled ? expandedItems : internalExpandedItems;
  };

  const currentExpandedItems = getCurrentExpandedItems();

  const isExpanded = (itemId: string): boolean => {
    return currentExpandedItems.includes(itemId);
  };

  const handleToggle = (itemId: string) => {
    let newExpandedItems: string[];

    if (groupContext) {
      // In a group: toggle single item, notify group
      const isCurrentlyExpanded =
        groupContext.activeAccordionId === accordionId && groupContext.activeItemId === itemId;
      groupContext.setActive(accordionId, isCurrentlyExpanded ? null : itemId);
      newExpandedItems = isCurrentlyExpanded ? [] : [itemId];
    } else if (allowMultiple) {
      // Multiple mode: add/remove from array
      newExpandedItems = isExpanded(itemId)
        ? currentExpandedItems.filter((id) => id !== itemId)
        : [...currentExpandedItems, itemId];
    } else {
      // Single mode: toggle or replace
      newExpandedItems = isExpanded(itemId) ? [] : [itemId];
    }

    if (!isControlled && !groupContext) {
      setInternalExpandedItems(newExpandedItems);
    }
    onExpandedItemsChange?.(newExpandedItems);
  };

  // Toggle from a click on the open content (legacy FAQ behaviour). Ignored for
  // disabled items and for clicks on interactive descendants (links, buttons,
  // form fields) so those keep working.
  const handleContentClick = (e: React.MouseEvent, item: AccordionItem) => {
    if (item.disabled) return;
    if ((e.target as HTMLElement).closest('a, button, input, select, textarea, label')) return;
    handleToggle(item.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    const enabledIndices = items
      .map((item, i) => (!item.disabled ? i : -1))
      .filter((i) => i !== -1);
    const currentEnabledIndex = enabledIndices.indexOf(index);

    let targetIndex: number | null = null;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (currentEnabledIndex < enabledIndices.length - 1) {
          targetIndex = enabledIndices[currentEnabledIndex + 1];
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (currentEnabledIndex > 0) {
          targetIndex = enabledIndices[currentEnabledIndex - 1];
        }
        break;
      case 'Home':
        e.preventDefault();
        targetIndex = enabledIndices[0];
        break;
      case 'End':
        e.preventDefault();
        targetIndex = enabledIndices[enabledIndices.length - 1];
        break;
    }

    if (targetIndex !== null) {
      triggerRefs.current[targetIndex]?.focus();
    }
  };

  return (
    <div
      className={`hi-ui-accordion hi-ui-accordion--${animation} ${className}`.trim()}
      style={style}
      data-testid="accordion"
    >
      {items.map((item, index) => {
        const triggerId = `${accordionId}-trigger-${item.id}`;
        const panelId = `${accordionId}-panel-${item.id}`;
        const expanded = isExpanded(item.id);

        return (
          <div
            key={item.id}
            className={`hi-ui-accordion__item ${expanded ? 'hi-ui-accordion__item--expanded' : ''} ${item.disabled ? 'hi-ui-accordion__item--disabled' : ''}`}
            data-testid={`accordion-item-${item.id}`}
          >
            <button
              ref={(el) => {
                triggerRefs.current[index] = el;
              }}
              id={triggerId}
              type="button"
              className="hi-ui-accordion__trigger"
              aria-expanded={expanded}
              aria-controls={panelId}
              aria-disabled={item.disabled || undefined}
              disabled={item.disabled}
              onClick={() => !item.disabled && handleToggle(item.id)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              data-testid={`accordion-trigger-${item.id}`}
            >
              <span className="hi-ui-accordion__label">{item.label}</span>
              {renderAccordionIcon(iconStyle, expanded)}
            </button>
            <div
              id={panelId}
              role={panelIsRegion ? 'region' : undefined}
              aria-labelledby={triggerId}
              className="hi-ui-accordion__panel"
              data-testid={`accordion-panel-${item.id}`}
            >
              <div
                className={`hi-ui-accordion__content${toggleOnContentClick ? ' hi-ui-accordion__content--clickable' : ''}`}
                onClick={toggleOnContentClick ? (e) => handleContentClick(e, item) : undefined}
              >
                {item.content}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
